import { HTMLProps, forwardRef, useMemo, useState } from 'react';

import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core';

import classNames from 'classnames';

import { SectionContext } from './SectionContext';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(4),
      position: 'relative',
    },
  });

export interface SectionRootProps
  extends Omit<HTMLProps<HTMLDivElement>, 'onChange'>,
    WithStyles<typeof styles> {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  defaultExpanded?: boolean;
}

const SectionRootMui = forwardRef<HTMLDivElement, SectionRootProps>(
  (props, ref) => {
    const {
      expanded: controlledExpanded,
      onExpandedChange,
      defaultExpanded = true,
      className,
      classes,
      children,
      ...rest
    } = props;

    // Handle both controlled and uncontrolled modes
    const [uncontrolledExpanded, setUncontrolledExpanded] =
      useState(defaultExpanded);

    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

    const contextValue = useMemo(
      () => ({
        expanded,
        onToggle: () => {
          const newExpanded = !expanded;
          if (isControlled) {
            onExpandedChange?.(newExpanded);
          } else {
            setUncontrolledExpanded(newExpanded);
          }
        },
      }),
      [expanded, isControlled, onExpandedChange],
    );

    return (
      <SectionContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={classNames(className, classes.root)}
          {...rest}
        >
          {children}
        </div>
      </SectionContext.Provider>
    );
  },
);

SectionRootMui.displayName = 'SectionMui.Root';

/** @deprecated Legacy MUI copy - migrate consumers to the Encore Section and delete. */
export default withStyles(styles)(SectionRootMui);
